import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserCog, Plus, Trash2, KeyRound, Loader2, Mail, Smartphone } from "lucide-react";

export default function AdminSubAdmins() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]); // [{slug, label, side, is_system}]
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({ full_name: "", email: "", mobile: "", password: "", role_slug: "admin" });

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: list }, { data: rolesData }] = await Promise.all([
        api.get("/admin/sub-admins"),
        api.get("/admin/custom-roles"),
      ]);
      setItems(list.items || []);
      // Show only assignable roles: admin + every custom role (built-in approver/user/partner are managed elsewhere)
      const assignable = [
        { slug: "admin", label: "Admin (full)", side: "admin", is_system: true },
        ...(rolesData.custom || []).map((c) => ({ slug: c.slug, label: c.name, side: c.side, is_system: false })),
      ];
      setRoles(assignable);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const roleLabelFor = (slug) => roles.find((r) => r.slug === slug)?.label || slug;
  const roleSideFor = (slug) => roles.find((r) => r.slug === slug)?.side || (slug === "admin" ? "admin" : (slug.startsWith("custom_partner_") ? "partner" : "admin"));

  const onCreate = async () => {
    if (!/^[6-9][0-9]{9}$/.test(form.mobile)) { toast.error("Enter a valid 10-digit mobile"); return; }
    if (form.password.length < 8) { toast.error("Password must be at least 8 chars"); return; }
    setBusy(true);
    try {
      await api.post("/admin/sub-admins", form);
      toast.success(`Sub-admin "${form.full_name}" created`);
      setOpenCreate(false);
      setForm({ full_name: "", email: "", mobile: "", password: "", role_slug: "admin" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not create sub-admin");
    } finally { setBusy(false); }
  };

  const onResetPassword = async () => {
    if (resetPwd.length < 8) { toast.error("Password must be at least 8 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/admin/sub-admins/${resetTarget.id}/reset-password`, { password: resetPwd });
      toast.success(`Password reset for ${resetTarget.email}`);
      setResetTarget(null);
      setResetPwd("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Reset failed");
    } finally { setBusy(false); }
  };

  const onDelete = async (u) => {
    if (!window.confirm(`Delete user ${u.email}? They will lose access immediately.`)) return;
    setDeletingId(u.id);
    try {
      await api.delete(`/admin/sub-admins/${u.id}`);
      toast.success(`User ${u.email} deleted`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally { setDeletingId(null); }
  };

  const protectedEmail = "admin@payflex.in";

  const filtered = useMemo(() => items, [items]);

  return (
    <DashboardLayout>
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <span className="overline">Functional Control · Users</span>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2">
            <UserCog className="h-7 w-7 text-primary" strokeWidth={1.5} /> Sub-admins
          </h1>
          <p className="text-muted-foreground mt-2 max-w-xl">
            Create users with the Admin role or any of your custom roles. They sign in with email/password OR mobile OTP — both supported.
          </p>
        </div>
        <Button onClick={() => setOpenCreate(true)} data-testid="open-create-sub-admin">
          <Plus className="h-4 w-4 mr-1" /> New sub-admin
        </Button>
      </div>

      <div className="mt-8 surface-card overflow-hidden" data-testid="sub-admins-table">
        <div className="px-5 py-3 bg-muted/40 border-b border-border flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">Active sub-admins ({filtered.length})</h3>
        </div>
        {loading ? (
          <div className="p-10 text-center text-muted-foreground"><Loader2 className="h-5 w-5 mx-auto animate-spin mb-2" /> Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <UserCog className="h-8 w-8 mx-auto text-muted-foreground" strokeWidth={1.5} />
            <p className="mt-3 text-sm text-muted-foreground">No sub-admins yet.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="p-3 pl-5">Name</th>
                <th className="p-3">Contact</th>
                <th className="p-3">Role</th>
                <th className="p-3">Created</th>
                <th className="p-3 text-right pr-5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => {
                const isSuper = u.email === protectedEmail;
                return (
                  <tr key={u.id} className="border-b border-border/50 last:border-0 hover:bg-accent/30" data-testid={`sub-admin-row-${u.id}`}>
                    <td className="p-3 pl-5">
                      <div className="font-medium">{u.full_name}</div>
                      {isSuper && <Badge data-testid="super-admin-badge" className="mt-1 bg-success text-success-foreground text-[9px] py-0 h-4">SUPER ADMIN</Badge>}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="flex items-center gap-1"><Mail className="h-3 w-3 text-muted-foreground" /> {u.email}</div>
                      <div className="flex items-center gap-1 mt-1 text-muted-foreground"><Smartphone className="h-3 w-3" /> +91 {u.mobile}</div>
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className="font-mono text-[10px]">{u.role}</Badge>
                      <div className="text-[11px] text-muted-foreground mt-1">{roleLabelFor(u.role)} · {roleSideFor(u.role)}</div>
                    </td>
                    <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{formatDate(u.created_at)}</td>
                    <td className="p-3 pr-5 text-right whitespace-nowrap">
                      <Button size="sm" variant="ghost" onClick={() => setResetTarget(u)}
                        data-testid={`reset-pwd-${u.id}`} className="hover:bg-accent">
                        <KeyRound className="h-3.5 w-3.5 mr-1" /> Reset password
                      </Button>
                      <Button size="sm" variant="ghost"
                        onClick={() => onDelete(u)}
                        disabled={isSuper || deletingId === u.id}
                        data-testid={`delete-sub-admin-${u.id}`}
                        className="text-destructive hover:text-destructive hover:bg-destructive/10 ml-1">
                        {deletingId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Create dialog */}
      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create sub-admin</DialogTitle>
            <DialogDescription>Sub-admin can log in via email + password OR mobile + OTP.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="sa-name" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })} data-testid="sa-email" />
              </div>
              <div>
                <Label>Mobile</Label>
                <Input maxLength={10} inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/\D/g, "") })} data-testid="sa-mobile" className="font-mono" placeholder="98xxxxxxxx" />
              </div>
            </div>
            <div>
              <Label>Initial password</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="sa-password" placeholder="≥ 8 chars" className="font-mono" />
              <p className="text-[11px] text-muted-foreground mt-1">Share with the user; they can rotate it later.</p>
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role_slug} onValueChange={(v) => setForm({ ...form, role_slug: v })}>
                <SelectTrigger data-testid="sa-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => <SelectItem key={r.slug} value={r.slug}>{r.label} <span className="text-[10px] text-muted-foreground ml-1">· {r.side}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenCreate(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={busy || !form.full_name || !form.email || !form.mobile || !form.password} data-testid="create-sa-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={!!resetTarget} onOpenChange={(o) => { if (!o) { setResetTarget(null); setResetPwd(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset password</DialogTitle>
            <DialogDescription>{resetTarget?.email}</DialogDescription>
          </DialogHeader>
          <div>
            <Label>New password</Label>
            <Input type="text" value={resetPwd} onChange={(e) => setResetPwd(e.target.value)} className="font-mono" placeholder="≥ 8 chars" data-testid="reset-pwd-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetTarget(null); setResetPwd(""); }}>Cancel</Button>
            <Button onClick={onResetPassword} disabled={busy || resetPwd.length < 8} data-testid="reset-pwd-submit">
              {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : "Reset"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
}
